import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Tables } from "@/integrations/supabase/types"
import { toast } from "sonner"
import { useAuth } from "./useAuth"

type Incident = Tables<"incidents">
type Profile = Tables<"profiles">
type Unit = Tables<"units">

type IncidentWithDetails = Incident & {
  reporter: Profile | null
  unit: Unit | null
  assigned_to_user: Profile | null
}

// Tipo para criação de nova ocorrência
type CreateIncidentData = {
  title: string
  description: string
  priority: string
  category?: string | null
  unit_id?: string | null
  assigned_to?: string | null
  photoFile?: File
}

export const useIncidents = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  try {
    const queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      data: [],
      isLoading: false,
      error: null
    }
  }
  
  return useQuery({
    queryKey: ["incidents", profile?.condominium_id, profile?.role, profile?.id],
    queryFn: async () => {
      if (!profile?.id) {
        console.log("DEBUG INCIDENTS: Usuário não autenticado")
        return []
      }

      console.log("DEBUG INCIDENTS: Buscando ocorrências para perfil:", {
        id: profile.id,
        role: profile.role,
        condominium_id: profile.condominium_id,
        full_name: profile.full_name
      })

      try {
        let query = supabase
          .from("incidents")
          .select(`
            *,
            reporter:profiles!incidents_reported_by_fkey(*),
            unit:units(*),
            assigned_to_user:profiles!incidents_assigned_to_fkey(*)
          `)

        // Filtrar por condomínio se não for superadmin
        if (profile.role !== 'superadmin') {
          query = query.eq('condominium_id', profile.condominium_id)
        }

        // Remover o filtro adicional para moradores - deixar o RLS controlar o acesso
        const { data, error } = await query.order("created_at", { ascending: false })

        if (error) {
          console.error("DEBUG INCIDENTS: Erro na consulta RLS:", error)
          console.error("DEBUG INCIDENTS: Detalhes do erro:", {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          })
          throw error
        }
        
        console.log("DEBUG INCIDENTS: Dados brutos das ocorrências:", data)
        console.log("DEBUG INCIDENTS: Total de ocorrências retornadas pelo RLS:", data?.length || 0)

        return data as IncidentWithDetails[]
      } catch (error) {
        console.error("DEBUG INCIDENTS: Erro geral na busca:", error)
        throw error
      }
    },
    enabled: !!profile?.id,
    retry: false
  })
}

export const useCreateIncident = () => {
  const { profile } = useAuth()
  
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return no-op mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: async () => ({}),
      isPending: false,
      isError: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async (data: CreateIncidentData) => {
      if (!profile?.condominium_id) throw new Error("Usuário sem condomínio associado")

      let photoUrl = null
      
      // Upload da foto se fornecida
      if (data.photoFile) {
        const fileExt = data.photoFile.name.split('.').pop()
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
        const filePath = `incidents/${fileName}`

        const { error: uploadError } = await supabase.storage
          .from('incident-photos')
          .upload(filePath, data.photoFile)

        if (uploadError) {
          console.error("Erro no upload da foto:", uploadError)
          throw new Error("Erro ao fazer upload da foto")
        }

        const { data: { publicUrl } } = supabase.storage
          .from('incident-photos')
          .getPublicUrl(filePath)

        photoUrl = publicUrl
      }

      const { photoFile, ...incidentData } = data

      const { data: incident, error } = await supabase
        .from("incidents")
        .insert({
          ...incidentData,
          condominium_id: profile.condominium_id,
          reported_by: profile.id,
          photos: photoUrl ? [photoUrl] : null,
          status: "open"
        })
        .select()
        .single()

      if (error) throw error
      return incident
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      toast.success("Ocorrência registrada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao registrar ocorrência:", error)
      toast.error("Erro ao registrar ocorrência")
    }
  })
}

export const useUpdateIncident = () => {
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return no-op mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: async () => ({}),
      isPending: false,
      isError: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<Incident>) => {
      const { data, error } = await supabase
        .from("incidents")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      toast.success("Ocorrência atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar ocorrência:", error)
      toast.error("Erro ao atualizar ocorrência")
    }
  })
}

export const useDeleteIncident = () => {
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return no-op mutation when QueryClient is not available
    return {
      mutate: () => {},
      mutateAsync: async () => ({}),
      isPending: false,
      isError: false,
      error: null
    }
  }

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("incidents")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      toast.success("Ocorrência excluída com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao excluir ocorrência:", error)
      toast.error("Erro ao excluir ocorrência")
    }
  })
}
